import { alpha, lighten, Theme, useTheme } from '@mui/material/styles';
import {
  cloneDeep,
  filter,
  flatMap,
  includes,
  isArray,
  isEmpty,
  isNumber,
  join,
  map,
  size,
  some,
  split,
  uniq,
} from 'es-toolkit/compat';
import React from 'react';
// @ts-ignore
import plotly from 'plotly.js/dist/plotly';
import Plot from 'react-plotly.js';

export interface ThemedPlotlyFigureData {
  data: Record<string, unknown>[];
  layout: Record<string, unknown>;
  frames?: Record<string, unknown>[];
}

type ThemeColorRole = 'primary' | 'secondary' | 'warning' | 'error' | 'highlight';

interface AxisPaddingModifiers {
  x?: number;
  y?: number;
}

interface PlotlyThemeModifiers {
  seriesColorRoles?: ThemeColorRole[];
  axisPadding?: AxisPaddingModifiers;
  bargap?: number;
}

type PlotlyTransitionOrdering = 'layout first' | 'traces first';

interface PlotlyTransitionConfig {
  duration?: number;
  easing?: string;
  ordering?: PlotlyTransitionOrdering;
}

const DEFAULT_PLOTLY_TRANSITION: Required<PlotlyTransitionConfig> = {
  duration: 300,
  easing: 'cubic-in-out',
  ordering: 'traces first',
};

const getThemeColorByRole = (theme: Theme, role: ThemeColorRole) => {
  if (role === 'highlight') {
    return theme.palette.custom?.highlight?.main || theme.palette.primary.main;
  }

  return theme.palette[role].main;
};

const buildChartPalette = (theme: Theme) => [
  theme.palette.primary.main,
  theme.palette.secondary.main,
  theme.palette.warning.main,
  theme.palette.error.main,
  theme.palette.custom?.highlight?.main || lighten(theme.palette.primary.main, 0.14),
  theme.palette.primary.light || lighten(theme.palette.primary.main, 0.2),
  theme.palette.secondary.light || lighten(theme.palette.secondary.main, 0.2),
  lighten(theme.palette.warning.main, theme.palette.mode === 'light' ? 0.08 : 0.18),
];

const TRACE_TYPES_WITH_TOP_LEVEL_LINE = ['scatter', 'scattergl', 'box'];

const getConfiguredColorway = (layout: Record<string, unknown>, fallbackColorway: string[]) => {
  if (isArray(layout.colorway) && !isEmpty(layout.colorway)) {
    return layout.colorway as string[];
  }

  return fallbackColorway;
};

const withFontColor = (existingValue: unknown, color: string, fontFamily?: string) => {
  const base = (existingValue as Record<string, unknown>) || {};
  return {
    ...base,
    color,
    ...(fontFamily ? { family: fontFamily } : {}),
  };
};

const AXIS_TITLE_WRAP_CHARS = 35;
const DEFAULT_TOP_MARGIN_WITH_TITLE = 56;
const DEFAULT_TOP_MARGIN_WITHOUT_TITLE = 24;
const DEFAULT_BOTTOM_MARGIN = 56;
// Pixels per extra line of x-axis title beyond the first.
const X_TITLE_EXTRA_PX_PER_LINE = 20;

const getResizeObserverTargets = (containerElement: HTMLElement): HTMLElement[] => [containerElement];

const hasPlotTitle = (titleValue: unknown): boolean => {
  if (typeof titleValue === 'string') {
    return Boolean(titleValue.trim());
  }

  if (!titleValue || typeof titleValue !== 'object') {
    return false;
  }

  const text = (titleValue as Record<string, unknown>).text;
  if (typeof text === 'string') {
    return Boolean(text.trim());
  }

  return Boolean(text);
};

/**
 * Wraps a long axis title string at word boundaries using Plotly's `<br>` tag,
 * so the label does not overflow or get clipped on narrow charts.
 */
const wrapAxisTitleText = (text: unknown): unknown => {
  if (typeof text !== 'string' || size(text) <= AXIS_TITLE_WRAP_CHARS) {
    return text;
  }

  const words = split(text, ' ');
  const lines: string[] = [];
  let current = '';

  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (size(candidate) > AXIS_TITLE_WRAP_CHARS && current) {
      lines.push(current);
      current = word;
    } else {
      current = candidate;
    }
  }

  if (current) {
    lines.push(current);
  }

  return join(lines, '<br>');
};

const withAxisTheme = (axisValue: unknown, theme: Theme) => {
  const base = (axisValue as Record<string, unknown>) || {};
  const title = (base.title as Record<string, unknown>) || {};

  return {
    ...base,
    automargin: true,
    gridcolor: alpha(theme.palette.divider, 0.35),
    linecolor: alpha(theme.palette.divider, 0.45),
    tickcolor: alpha(theme.palette.divider, 0.55),
    ticklen: 4,
    tickfont: withFontColor(base.tickfont, theme.palette.text.secondary, theme.typography.fontFamily),
    title: {
      ...title,
      text: wrapAxisTitleText(title.text),
      font: withFontColor(title.font, theme.palette.text.primary, theme.typography.fontFamily),
      standoff: title.standoff || 14,
    },
    zerolinecolor: alpha(theme.palette.divider, 0.45),
  };
};

const getThemeModifiers = (figure: ThemedPlotlyFigureData): PlotlyThemeModifiers => {
  const layout = (figure.layout as Record<string, unknown>) || {};
  const meta = (layout.meta as Record<string, unknown>) || {};
  return ((meta.themeModifiers as PlotlyThemeModifiers) || {}) as PlotlyThemeModifiers;
};

const traceHasOwnColorEncoding = (trace: Record<string, unknown>) => {
  const marker = ((trace.marker as Record<string, unknown>) || {}) as Record<string, unknown>;

  return Boolean(
    ((trace.line as Record<string, unknown>) || {}).color ||
      marker.color ||
      trace.coloraxis ||
      trace.colorscale ||
      marker.coloraxis ||
      marker.colorscale ||
      marker.autocolorscale ||
      (isArray(marker.colors) && !isEmpty(marker.colors))
  );
};

const collectNumericValues = (values: unknown): number[] => {
  if (!Array.isArray(values)) {
    return [];
  }

  return filter(values, (value): value is number => isNumber(value) && Number.isFinite(value));
};

const buildAxisRangeWithPadding = (values: number[], paddingFraction: number): [number, number] | undefined => {
  if (isEmpty(values)) {
    return undefined;
  }

  const minimum = Math.min(...values);
  const maximum = Math.max(...values);

  if (minimum >= 0) {
    if (maximum === 0) {
      return [0, 1];
    }

    return [0, maximum + maximum * paddingFraction];
  }

  const span = maximum - minimum || Math.max(Math.abs(maximum), Math.abs(minimum), 1);
  return [minimum - span * paddingFraction, maximum + span * paddingFraction];
};

const withAxisPadding = (
  axisValue: unknown,
  data: Record<string, unknown>[],
  axisKey: 'x' | 'y',
  paddingFraction?: number
) => {
  const base = (axisValue as Record<string, unknown>) || {};
  if (!paddingFraction || Array.isArray(base.range)) {
    return base;
  }

  const numericValues = flatMap(data, (trace) => collectNumericValues(trace[axisKey]));
  const paddedRange = buildAxisRangeWithPadding(numericValues, paddingFraction);
  if (!paddedRange) {
    return base;
  }

  return {
    ...base,
    range: paddedRange,
  };
};

export const applyThemeToPlotlyFigure = (figure: ThemedPlotlyFigureData, theme: Theme): ThemedPlotlyFigureData => {
  const themedFigure = cloneDeep(figure);
  const themeModifiers = getThemeModifiers(themedFigure);
  const chartPalette = buildChartPalette(theme);
  const layout = ((themedFigure.layout as Record<string, unknown>) || {}) as Record<string, unknown>;
  const colorway = getConfiguredColorway(layout, chartPalette);
  const hasBarLikeTrace = some(themedFigure.data, (trace) => includes(['bar', 'histogram'], String(trace.type || '')));

  themedFigure.data = map(themedFigure.data, (trace, index) => {
    const traceType = String(trace.type || '');
    const marker = ((trace.marker as Record<string, unknown>) || {}) as Record<string, unknown>;
    const explicitColorRole = themeModifiers.seriesColorRoles?.[index];
    const roleColor = explicitColorRole ? getThemeColorByRole(theme, explicitColorRole) : undefined;

    if (traceType === 'pie') {
      return {
        ...trace,
        marker: {
          ...marker,
          colors: isArray(marker.colors) && !isEmpty(marker.colors) ? marker.colors : colorway,
          line: {
            color: alpha(theme.palette.background.paper, 0.9),
            width: 1,
          },
        },
        textfont: withFontColor(trace.textfont, theme.palette.text.primary, theme.typography.fontFamily),
      };
    }

    const line = ((trace.line as Record<string, unknown>) || {}) as Record<string, unknown>;

    // Prevent histograms with a single unique value from spanning the full axis
    const histogramBinFix: Record<string, unknown> = {};
    if (traceType === 'histogram' && !trace.xbins) {
      const xValues = collectNumericValues(trace.x);
      const uniqueValues = uniq(xValues);
      if (size(uniqueValues) === 1 && !isEmpty(xValues)) {
        const val = uniqueValues[0];
        const binSize = Math.max(Math.abs(val) * 0.05, 1);
        histogramBinFix.xbins = { start: val - binSize / 2, end: val + binSize / 2, size: binSize };
      }
    }

    // Prevent bar charts with a single unique x from spanning the full axis
    const barWidthFix: Record<string, unknown> = {};
    if (traceType === 'bar' && trace.width === undefined) {
      const xValues = isArray(trace.x) ? trace.x : [];
      const uniqueX = uniq(xValues);
      if (size(uniqueX) === 1 && !isEmpty(xValues)) {
        const val = Number(uniqueX[0]);
        if (Number.isFinite(val)) {
          barWidthFix.width = Math.max(Math.abs(val) * 0.05, 1);
        }
      }
    }

    if (!roleColor || traceHasOwnColorEncoding(trace)) {
      return {
        ...trace,
        ...histogramBinFix,
        ...barWidthFix,
      };
    }

    const themedTrace: Record<string, unknown> = {
      ...trace,
      ...histogramBinFix,
      ...barWidthFix,
      marker: {
        ...marker,
        color: marker.color || roleColor,
      },
    };

    if (includes(TRACE_TYPES_WITH_TOP_LEVEL_LINE, traceType)) {
      themedTrace.line = {
        ...line,
        color: line.color || roleColor,
      };
    }

    return themedTrace;
  });

  const themedXAxis = withAxisPadding(
    withAxisTheme(layout.xaxis, theme),
    (themedFigure.data || []) as Record<string, unknown>[],
    'x',
    themeModifiers.axisPadding?.x
  );
  const themedYAxis = withAxisPadding(
    withAxisTheme(layout.yaxis, theme),
    (themedFigure.data || []) as Record<string, unknown>[],
    'y',
    themeModifiers.axisPadding?.y
  );
  const layoutHoverlabel = ((layout.hoverlabel as Record<string, unknown>) || {}) as Record<string, unknown>;

  themedFigure.layout = {
    ...layout,
    colorway,
    paper_bgcolor: 'transparent',
    plot_bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.7 : 0.18),
    font: withFontColor(layout.font, theme.palette.text.primary, theme.typography.fontFamily),
    hoverlabel: {
      bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.98 : 0.94),
      bordercolor: alpha(theme.palette.divider, theme.palette.mode === 'light' ? 0.95 : 0.85),
      ...layoutHoverlabel,
      font: withFontColor(layoutHoverlabel.font, theme.palette.text.primary, theme.typography.fontFamily),
    },
    legend: {
      ...((layout.legend as Record<string, unknown>) || {}),
      bgcolor: 'transparent',
      font: withFontColor(
        ((((layout.legend as Record<string, unknown>) || {}).font as Record<string, unknown>) || {}) as Record<
          string,
          unknown
        >,
        theme.palette.text.primary,
        theme.typography.fontFamily
      ),
    },
    margin: {
      l: 48,
      r: 24,
      t: hasPlotTitle(layout.title) ? DEFAULT_TOP_MARGIN_WITH_TITLE : DEFAULT_TOP_MARGIN_WITHOUT_TITLE,
      b: DEFAULT_BOTTOM_MARGIN,
      ...((layout.margin as Record<string, unknown>) || {}),
    },
    bargap: layout.bargap ?? themeModifiers.bargap ?? (hasBarLikeTrace ? 0.24 : undefined),
    bargroupgap: layout.bargroupgap ?? (hasBarLikeTrace ? 0.08 : undefined),
    xaxis: themedXAxis,
    yaxis: themedYAxis,
  };

  return themedFigure;
};

const withPlotlyTransition = (
  figure: ThemedPlotlyFigureData,
  transitionConfig?: PlotlyTransitionConfig
): ThemedPlotlyFigureData => {
  const layout = (figure.layout as Record<string, unknown>) || {};
  const existingTransition = (layout.transition as Record<string, unknown>) || {};

  return {
    ...figure,
    layout: {
      ...layout,
      transition: {
        ...DEFAULT_PLOTLY_TRANSITION,
        ...existingTransition,
        ...(transitionConfig || {}),
      },
    },
  };
};

/**
 * Counts how many lines the x-axis title occupies after `wrapAxisTitleText`
 * has inserted `<br>` tags.
 */
const countXAxisTitleLines = (figure: ThemedPlotlyFigureData): number => {
  const layout = (figure.layout as Record<string, unknown>) || {};
  const xaxis = (layout.xaxis as Record<string, unknown>) || {};
  const title = (xaxis.title as Record<string, unknown>) || {};
  const text = title.text;
  if (typeof text !== 'string' || !text) return 1;
  return size(split(text, '<br>'));
};

export interface ThemedPlotlyFigureProps {
  figure: ThemedPlotlyFigureData;
  height?: number | string;
  preservePlotAreaHeight?: boolean;
  exportFileName?: string;
  onPlotlyInit?: (el: HTMLElement) => void;
  onPointClick?: (event: unknown) => void;
  showModeBar?: boolean;
  animateOnUpdate?: boolean;
  transitionConfig?: PlotlyTransitionConfig;
}

export interface ThemedPlotlyFigureHandle {
  exportImage: () => void;
}

const ThemedPlotlyFigure = React.forwardRef<ThemedPlotlyFigureHandle, ThemedPlotlyFigureProps>(
  (
    {
      figure,
      height = 320,
      preservePlotAreaHeight = false,
      exportFileName,
      onPlotlyInit,
      onPointClick,
      showModeBar = false,
      animateOnUpdate = false,
      transitionConfig,
    },
    ref
  ) => {
    const theme = useTheme();
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const graphDivRef = React.useRef<HTMLElement | null>(null);
    const previousFigureRef = React.useRef<ThemedPlotlyFigureData | null>(null);
    const resizeFrameRef = React.useRef<number | null>(null);
    const [hasInitializedPlot, setHasInitializedPlot] = React.useState(false);
    const [containerWidth, setContainerWidth] = React.useState<number | null>(null);
    const shouldAnimateFigureUpdate =
      animateOnUpdate &&
      hasInitializedPlot &&
      previousFigureRef.current !== null &&
      previousFigureRef.current !== figure;
    const themedFigure = React.useMemo(() => {
      const nextFigure = applyThemeToPlotlyFigure(figure, theme);

      return shouldAnimateFigureUpdate ? withPlotlyTransition(nextFigure, transitionConfig) : nextFigure;
    }, [figure, shouldAnimateFigureUpdate, theme, transitionConfig]);

    React.useEffect(() => {
      previousFigureRef.current = figure;
    }, [figure]);

    const syncContainerWidth = React.useCallback(() => {
      const containerElement = containerRef.current;
      if (!containerElement) {
        return;
      }

      const measuredWidth = Math.round(containerElement.getBoundingClientRect().width || 0);
      if (measuredWidth <= 0) {
        return;
      }

      setContainerWidth((currentWidth) => (currentWidth === measuredWidth ? currentWidth : measuredWidth));
    }, []);

    const { containerHeight, renderedLayout } = React.useMemo(() => {
      const layout = ((themedFigure.layout as Record<string, unknown>) || {}) as Record<string, unknown>;
      const widthAwareLayout = containerWidth && containerWidth > 0 ? { ...layout, width: containerWidth } : layout;

      if (!preservePlotAreaHeight || typeof height !== 'number') {
        return { containerHeight: height, renderedLayout: widthAwareLayout };
      }

      const existingMargin = (layout.margin as Record<string, unknown>) || {};
      const topMargin = typeof existingMargin.t === 'number' ? existingMargin.t : DEFAULT_TOP_MARGIN_WITHOUT_TITLE;
      const bottomMargin = typeof existingMargin.b === 'number' ? existingMargin.b : DEFAULT_BOTTOM_MARGIN;
      const xTitleLines = countXAxisTitleLines(themedFigure);
      const extraBottom = Math.max(0, xTitleLines - 1) * X_TITLE_EXTRA_PX_PER_LINE;
      const totalHeight = height + topMargin + bottomMargin + extraBottom;

      return {
        containerHeight: totalHeight,
        renderedLayout: {
          ...widthAwareLayout,
          autosize: false,
          height: totalHeight,
          margin: {
            ...existingMargin,
            t: topMargin,
            b: bottomMargin + extraBottom,
          },
        },
      };
    }, [containerWidth, height, preservePlotAreaHeight, themedFigure]);

    const resizePlotToContainer = React.useCallback(() => {
      if (typeof window === 'undefined') {
        return;
      }

      if (resizeFrameRef.current !== null) {
        window.cancelAnimationFrame(resizeFrameRef.current);
      }

      resizeFrameRef.current = window.requestAnimationFrame(() => {
        const currentGraphDiv = graphDivRef.current;
        resizeFrameRef.current = null;

        if (!currentGraphDiv) {
          return;
        }

        try {
          plotly.Plots.resize(currentGraphDiv);
        } catch (_error) {
          // Ignore transient Plotly resize failures during mount/unmount races.
        }
      });
    }, []);

    const handlePlotlyGraphDiv = React.useCallback(
      (graphDiv: HTMLElement) => {
        graphDivRef.current = graphDiv;
        setHasInitializedPlot(true);
        onPlotlyInit?.(graphDiv);
        syncContainerWidth();
        resizePlotToContainer();
      },
      [onPlotlyInit, resizePlotToContainer, syncContainerWidth]
    );

    React.useImperativeHandle(
      ref,
      () => ({
        exportImage: () => {
          if (!graphDivRef.current) {
            return;
          }

          plotly.downloadImage(graphDivRef.current, {
            format: 'png',
            filename: exportFileName || 'plot',
            scale: 2,
          });
        },
      }),
      [exportFileName]
    );

    React.useEffect(() => {
      const containerElement = containerRef.current;
      if (!containerElement) {
        return undefined;
      }

      syncContainerWidth();
      resizePlotToContainer();

      if (typeof ResizeObserver === 'undefined') {
        return () => {
          if (resizeFrameRef.current !== null) {
            window.cancelAnimationFrame(resizeFrameRef.current);
          }
        };
      }

      const resizeObserver = new ResizeObserver(() => {
        syncContainerWidth();
        resizePlotToContainer();
      });

      for (const resizeTarget of getResizeObserverTargets(containerElement)) {
        resizeObserver.observe(resizeTarget);
      }

      return () => {
        resizeObserver.disconnect();
        if (resizeFrameRef.current !== null) {
          window.cancelAnimationFrame(resizeFrameRef.current);
        }
      };
    }, [containerHeight, resizePlotToContainer, syncContainerWidth]);

    return (
      <div
        ref={containerRef}
        style={{
          width: '100%',
          maxWidth: '100%',
          height: containerHeight,
          minWidth: 0,
          position: 'relative',
          zIndex: 1,
          overflow: 'visible',
        }}
      >
        <Plot
          data={themedFigure.data as never[]}
          layout={renderedLayout as never}
          frames={themedFigure.frames as never[] | undefined}
          config={{
            displayModeBar: showModeBar,
            displaylogo: false,
            responsive: true,
            toImageButtonOptions: {
              format: 'png',
              filename: exportFileName || 'plot',
              scale: 2,
            },
          }}
          style={{
            width: '100%',
            height: '100%',
            minWidth: 0,
            cursor: onPointClick ? 'pointer' : 'default',
            overflow: 'visible',
          }}
          useResizeHandler
          onInitialized={(_figure, graphDiv) => handlePlotlyGraphDiv(graphDiv as HTMLElement)}
          onUpdate={(_figure, graphDiv) => handlePlotlyGraphDiv(graphDiv as HTMLElement)}
          onClick={onPointClick as never}
        />
      </div>
    );
  }
);

ThemedPlotlyFigure.displayName = 'ThemedPlotlyFigure';

export default ThemedPlotlyFigure;
